// Section 19 and 16: Dispute. 6% per season, any season. Two named people from different
// households are in conflict. Choices: side with the first or the second (the loser's household
// loses trust -8) or mediate (offered only when both trust you 60+; both lose trust -2).
// Constants: config.events.dispute.
import type { Config } from '../../config';
import {
  adultsOf,
  choiceOutcome,
  eventEntry,
  eventPrompt,
  eventRng,
} from '../../core/content-helpers';
import { defineEvent, noModifiers, type EventChoiceDef } from '../../core/registry';
import type { Delta, EventPrompt, Person, ReadContext } from '../../core/types';

function parties(ctx: ReadContext): [Person, Person] | null {
  const adults = adultsOf(ctx, (p) => p.status.guest === null);
  const rng = eventRng(ctx, 'dispute', 'parties');
  if (adults.length < 2) return null;
  const a = rng.pick(adults);
  const others = adults.filter((p) => p.householdId !== a.householdId);
  if (others.length === 0) return null;
  return [a, rng.pick(others)];
}

function subject(ctx: ReadContext, prompt: EventPrompt, i: number): Person | undefined {
  const id = prompt.subjectIds[i];
  return id === undefined ? undefined : ctx.state.people[id];
}

export function dispute(config: Config) {
  const c = config.events.dispute;
  const householdLoses = (ctx: ReadContext, loser: Person | undefined): Delta[] => {
    if (loser === undefined) return [];
    const members = ctx.state.households[loser.householdId]?.memberIds ?? [loser.id];
    return members.map((personId) => ({
      kind: 'person' as const,
      personId,
      field: 'trust' as const,
      amount: c.loserTrust,
      source: 'event:dispute',
    }));
  };
  const side = (id: 'side-a' | 'side-b', label: string, win: number): EventChoiceDef => ({
    id,
    label,
    preview: (ctx, prompt) => {
      const loser = subject(ctx, prompt, 1 - win);
      const size =
        loser === undefined ? 0 : (ctx.state.households[loser.householdId]?.memberIds.length ?? 1);
      return [
        {
          key: 'dispute-side',
          params: { name: loser?.name ?? '', people: size, trust: c.loserTrust },
        },
      ];
    },
    apply: (ctx, prompt) => {
      const winner = subject(ctx, prompt, win);
      const loser = subject(ctx, prompt, 1 - win);
      return [
        ...householdLoses(ctx, loser),
        choiceOutcome(prompt, id, { winner: winner?.name ?? '', loser: loser?.name ?? '' }),
      ];
    },
  });
  const choices: EventChoiceDef[] = [
    side('side-a', 'Side with the first', 0),
    side('side-b', 'Side with the second', 1),
    {
      id: 'mediate',
      label: 'Mediate',
      preview: () => [
        { key: 'dispute-mediate', params: { trust: c.mediateTrust, min: c.mediateMinTrust } },
      ],
      apply: (ctx, prompt) => {
        const out: Delta[] = [];
        for (const p of [subject(ctx, prompt, 0), subject(ctx, prompt, 1)])
          if (p !== undefined)
            out.push({
              kind: 'person',
              personId: p.id,
              field: 'trust',
              amount: c.mediateTrust,
              source: 'event:dispute',
            });
        out.push(choiceOutcome(prompt, 'mediate'));
        return out;
      },
    },
  ];
  return defineEvent({
    id: 'dispute',
    label: 'Dispute',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['spring', 'summer', 'autumn', 'winter'],
    foreshadow: null,
    ledgerTag: null,
    weight: (ctx) => (parties(ctx) === null ? 0 : c.baseChance),
    text: (ctx) => {
      const p = parties(ctx);
      return eventEntry('dispute', { a: p?.[0].name ?? '', b: p?.[1].name ?? '' });
    },
    apply: (ctx) => {
      const p = parties(ctx);
      if (p === null) return [];
      const [a, b] = p;
      const canMediate = a.trust >= c.mediateMinTrust && b.trust >= c.mediateMinTrust;
      return [
        eventPrompt(ctx, 'dispute', choices, {
          params: { a: a.name, b: b.name },
          subjectIds: [a.id, b.id],
          defaultChoiceId: canMediate ? 'mediate' : 'side-a',
          offered: canMediate ? ['side-a', 'side-b', 'mediate'] : ['side-a', 'side-b'],
        }),
      ];
    },
    choices,
    modifierTargets: [],
    modifiers: noModifiers,
  });
}
